import { LcfsRinValidation } from '../../helper/validation/validation-sub-modules/lcfs-rin.validation';
import { UnitValueValidation } from '../../helper/validation/validation-sub-modules/unit-value.validation';
import Utils from '../../util';
import { ProjectValidationUnit } from '../data-structures/project-validation';
import { UnitValue } from '../data-structures/unit-value.model';

export interface RinLcfsInterface {
  rinD3: UnitValue;
  rinD5: UnitValue;
  carbonBaseline: UnitValue;
  carbonIntensity: UnitValue;
  averagePrice: UnitValue;
  lhvCh4: UnitValue;
  natgasCommodity: UnitValue;
  lcfsEnabled: boolean;
  rinEnabled: boolean;
  natgasEnabled: boolean;
}

export class LcfsRin {
  rinD3: UnitValue;
  rinD5: UnitValue;
  carbonBaseline: UnitValue;
  carbonIntensity: UnitValue;
  averagePrice: UnitValue;
  lhvCh4: UnitValue;
  natgasCommodity: UnitValue;
  lcfsEnabled: boolean;
  rinEnabled: boolean;
  natgasEnabled: boolean;

  constructor(param: RinLcfsInterface = {
    rinD3: new UnitValue(),
    rinD5: new UnitValue(),
    carbonBaseline: new UnitValue(),
    carbonIntensity: new UnitValue(),
    averagePrice: new UnitValue(),
    lhvCh4: new UnitValue(),
    natgasCommodity: new UnitValue(),
    lcfsEnabled: true,
    rinEnabled: true,
    natgasEnabled: false
  }) {
    this.rinD3 = new UnitValue(param.rinD3);
    this.rinD5 = new UnitValue(param.rinD5);
    this.carbonBaseline = new UnitValue(param.carbonBaseline);
    this.carbonIntensity = new UnitValue(param.carbonIntensity);
    this.averagePrice = new UnitValue(param.averagePrice);
    this.lhvCh4 = new UnitValue(param.lhvCh4);
    this.natgasCommodity = new UnitValue(param.natgasCommodity);
    // should be enabled by default in case previous values present, calculations would be affect if false
    if (param.lcfsEnabled === undefined) {
      // backwards compatibility. can't go into validation because of circ dependencies
      const validation = this.getLcfsValidation();
      if (validation.isValid) {
        this.lcfsEnabled = true;
      }
    } else {
      this.lcfsEnabled = param.lcfsEnabled;
    }
    // should be enabled by default in case previous values present, calculations would be affect if false
    if (param.rinEnabled === undefined) {
      const rinValidation = this.getRinValidation();
      if (rinValidation.isValid) {
        this.rinEnabled = true;
      }
    } else {
      this.rinEnabled = param.rinEnabled;
    }
    // no value for natgas from old projects so has to be disable by default to prevent potential errors
    if (param.natgasEnabled === undefined) {
      const natGasValidation = this.getNatGasValidation();
      if (natGasValidation.isValid) {
        this.natgasEnabled = true;
      }
    } else {
      this.natgasEnabled = param.natgasEnabled;
    }
  }

  private getLcfsValidation(validationUnit = new ProjectValidationUnit()) {
    validationUnit.addModuleResults(
      new UnitValueValidation(this.carbonBaseline).getValidation(),
      LcfsRinPropName.carbonBaseline
    );
    validationUnit.addModuleResults(
      new UnitValueValidation(this.carbonIntensity).getValidation(),
      LcfsRinPropName.carbonIntensity
    );
    validationUnit.addModuleResults(
      new UnitValueValidation(this.averagePrice).getValidation(),
      LcfsRinPropName.averagePrice
    );

    return validationUnit;
  }

  private getNatGasValidation(validationUnit = new ProjectValidationUnit()) {
      validationUnit.addModuleResults(
        new UnitValueValidation(this.natgasCommodity).getValidation(),
        LcfsRinPropName.natgasCommodity
      );
      return validationUnit;
  }

  private getRinValidation(validationUnit = new ProjectValidationUnit()) {
    if (Utils.isNumeric(this.rinD3.value)) {
      validationUnit.addModuleResults(
        new UnitValueValidation(this.rinD3).getValidation(),
        LcfsRinPropName.rinD3
      );
    } else {
      validationUnit.addModuleResults(
        new UnitValueValidation(this.rinD5).getValidation(),
        LcfsRinPropName.rinD5
      );
    }
    return validationUnit;
  }

  static deepCopy(rinLcfs: LcfsRin) {
    let copy;
    if (rinLcfs) {
      copy = new LcfsRin(rinLcfs);
      return copy;
    }
    throw new Error('RIN LCFS copy failed');
  }
}

export enum LcfsRinPropName {
  rinD3 = 'PROJECT.FINANCIAL_GEN_PROD.RIN.RIN_D3',
  rinD5 = 'PROJECT.FINANCIAL_GEN_PROD.RIN.RIN_D5',
  cwcPrice = 'PROJECT.FINANCIAL_GEN_PROD.RIN.EPA',
  carbonBaseline = 'PROJECT.FINANCIAL_GEN_PROD.LCFS.CI',
  carbonIntensity = 'PROJECT.FINANCIAL_GEN_PROD.LCFS.PCI',
  averagePrice = 'PROJECT.FINANCIAL_GEN_PROD.LCFS.AVG',
  lhvCh4 = 'PROJECT.FINANCIAL_GEN_PROD.LCFS.LHV_CH4',
  natgasCommodity = 'PROJECT.FINANCIAL_GEN_PROD.NATGAS_COMMODITY.NATGAS_COMMODITY',
  id_rinD3 = 'RIN (code D3)',
  id_rinD5 = 'RIN (code D5)',
  id_cwcPrice = 'EPA cellulosic waiver credit (CWC) price',
  id_carbonBaseline = 'carbon intensity of baseline',
  id_carbonIntensity = 'plant carbon intensity',
  id_averagePrice = 'average price of LCFS credit',
  id_lhvCh4 = 'LHV CH4',
  id_natgasCommodity = 'commodity price of natural gas'
}
